import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import type React from 'react';
import { useMemo, useState } from 'react';
import ActionsColumn from './invoice/ActionsColumn';
import InvoicePaymentsDialog from './InvoicePaymentsDialog';
import InvoicePdfDialog from './InvoicePdfDialog';
import { formatWithCurrency } from 'utils-ts/sales-and-invoicing';
import { type Invoice, invoicePaymentOptions, systemDefaultValues } from '@a1/shared';
import MarkAsPaidDialog from './MarkAsPaidDialog';
import useInvoiceStatusActions from 'hooks-ts/useInvoiceStatusActions';
import { invoiceStatusAction } from 'constants-ts/payments';
import { type Booking } from 'types/booking';
import VoidDialog from './VoidDialog';
import SendBillingDocumentPdfDialog from './SendBillingDocumentPdfDialog';

interface InvoicesDialogProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  invoices: Invoice[];
  setInvoices: React.Dispatch<React.SetStateAction<Invoice[]>>;
  setBookings?: React.Dispatch<React.SetStateAction<Booking[]>>;
}

const BookingInvoicesDialog = ({ open, setOpen, invoices, setInvoices, setBookings }: InvoicesDialogProps) => {
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [openInvoicePaymentsDialog, setOpenInvoicePaymentsDialog] = useState<boolean>(false);
  const [openInvoicePdfDialog, setOpenInvoicePdfDialog] = useState<boolean>(false);
  const [openSendInvoicePdfDialog, setOpenSendInvoicePdfDialog] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [openMarkAsPaidDialog, setOpenMarkAsPaidDialog] = useState<boolean>(false);
  const [openVoidDialog, setOpenVoidDialog] = useState<boolean>(false);
  const [externalRefNo, setExternalRefNo] = useState<string>('');
  const [externalSource, setExternalSource] = useState<string>('');

  const { processInvoiceStatusChange } = useInvoiceStatusActions({
    setOpenMarkAsPaidDialog,
    setOpenVoidDialog,
    setSelectedInvoice,
    setExternalRefNo,
    setExternalSource,
    setLoading,
    setInvoices,
    setBookings
  });

  const handleMarkAsPaid = async (invoiceId: number, externalRefNo: string, externalSource: string) => {
    await processInvoiceStatusChange(invoiceId, invoiceStatusAction.MARK_AS_PAID, { externalRefNo, externalSource });
  };

  const handleVoid = async (invoiceId: number) => {
    await processInvoiceStatusChange(invoiceId, invoiceStatusAction.VOID);
  };

  const columns = useMemo(() => {
    const columns: GridColDef<(typeof invoices)[number]>[] = [
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        minWidth: 100,
        renderCell: (params) => {
          const invoice = params.row;
          return (
            <ActionsColumn
              invoice={invoice}
              setSelectedInvoice={setSelectedInvoice}
              setOpenInvoicePaymentsDialog={setOpenInvoicePaymentsDialog}
              setOpenInvoicePdfDialog={setOpenInvoicePdfDialog}
              setOpenSendInvoicePdfDialog={setOpenSendInvoicePdfDialog}
              setOpenMarkAsPaidDialog={setOpenMarkAsPaidDialog}
              setOpenVoidDialog={setOpenVoidDialog}
            />
          );
        }
      },
      {
        field: 'invoiceNumber',
        headerName: 'Number',
        flex: 1,
        minWidth: 150
      },
      {
        field: 'billedAmount',
        headerName: 'Billed Amount',
        flex: 1,
        minWidth: 150,
        valueFormatter: (_, row) => formatWithCurrency(row.billedAmount, row.currency ?? systemDefaultValues.currency)
      },
      {
        field: 'paymentOption',
        headerName: 'Payment Option',
        flex: 1,
        minWidth: 150,
        valueFormatter: (_, row) => (row.paymentOption ? invoicePaymentOptions[row.paymentOption]?.label : '-')
      },
      { field: 'status', headerName: 'Status', flex: 1 }
    ];
    return columns;
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Dialog maxWidth="md" fullWidth open={open} onClose={handleClose}>
        <DialogContent>
          <DataGrid columns={columns} rows={invoices ?? []} disableRowSelectionOnClick />
        </DialogContent>
      </Dialog>

      <InvoicePaymentsDialog
        open={openInvoicePaymentsDialog}
        setOpen={setOpenInvoicePaymentsDialog}
        invoiceIds={[selectedInvoice?.id]}
        setDisplayInvoices={setInvoices}
        setBookings={setBookings}
      />

      {/* To deprecate, use PdfViewer Dialog instead */}
      <InvoicePdfDialog
        open={openInvoicePdfDialog}
        setOpen={setOpenInvoicePdfDialog}
        invoice={selectedInvoice}
        setOpenInvoiceSend={setOpenSendInvoicePdfDialog}
      />

      <SendBillingDocumentPdfDialog
        open={openSendInvoicePdfDialog}
        setOpen={setOpenSendInvoicePdfDialog}
        billingDocument={selectedInvoice}
      />

      <MarkAsPaidDialog
        open={openMarkAsPaidDialog}
        onClose={() => {
          setOpenMarkAsPaidDialog(false);
        }}
        onMarkAsPaid={handleMarkAsPaid}
        targetInfo={{
          id: selectedInvoice?.id ?? '',
          displayIdentifier: `Invoice (${selectedInvoice?.invoiceNumber})`
        }}
        setExternalRefNo={setExternalRefNo}
        setExternalSource={setExternalSource}
        externalRefNo={externalRefNo}
        externalSource={externalSource}
        loading={loading}
      />

      <VoidDialog
        open={openVoidDialog}
        onClose={() => {
          setOpenVoidDialog(false);
        }}
        onVoid={handleVoid}
        targetInfo={{
          id: selectedInvoice?.id ?? '',
          displayIdentifier: `Invoice (${selectedInvoice?.invoiceNumber})`
        }}
        loading={loading}
      />
    </>
  );
};

export default BookingInvoicesDialog;
